



































import Vue from 'vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'

const { VUE_APP_ENV } = process.env

import DevGrid from '@/components/DevGrid.vue'
import EnvSwitcher from '@/components/EnvSwitcher.vue'
import ChromeHeader from '@/components/chrome/Header.vue'
import ChromeFooter from '@/components/chrome/Footer.vue'
import Curtain from '@/components/TransitionCurtain.vue'

import NotFound from '@/views/NotFound.vue'

import { StorageUtils } from '@/inc/plugins/storage'
import { isUnderMaintenance } from '@/inc/app.config'
import favicon from '@/inc/custom/favicon'
import { getScrollBarWidth } from '@/inc/utils/getScrollBarWidth'
import { motionOK } from '@/inc/utils/motionOK'

export default Vue.extend({
  name: 'app',
  components: {
    DevGrid,
    EnvSwitcher,
    NotFound,
    ChromeHeader,
    ChromeFooter,
    Curtain,
  },
  data() {
    return {
      hasError: false,
      isUnderMaintenance,
      body: {} as HTMLElement,
    }
  },
  computed: {
    ...mapGetters(['meta', 'chrome', 'template']),
    isHome() {
      return this.$route.name === 'Home'
    },
  },
  methods: {
    ...mapMutations({
      setResource: 'SET_RESOURCE',
      flagOnboarding: 'FLAG_ONBOARDING',
    }),
    ...mapMutations('ui', { transitionStatus: 'TRANSITION_STATUS' }),
    ...mapActions(['fetchChrome']),
    enter(_el: HTMLElement, done: () => void) {
      if (motionOK()) {
        document.body.classList.remove('is-transitioning')

        // Hide the curtain, then animate in page content
        this.$refs.curtain.animateOut().then(done)
      } else {
        // Since scrollTo is usually handled within animation
        // We have to manually scollTo here for user who don't want animation
        window.scrollTo(0, 0)
        done()
      }
    },
    leave(_el: HTMLElement, done: () => void) {
      if (motionOK()) {
        document.body.classList.add('is-transitioning')

        this.$refs.curtain.animateIn().then(() => {
          if (this.$refs.header.megaMenuOpened) {
            this.$refs.header.toggleMegaMenu()
          }

          window.scrollTo(0, 0)
          done()
        })
      } else {
        done()
      }
    },
    beforeLeave() {
      this.transitionStatus('leaving')
    },
    beforeEnter() {
      this.transitionStatus('entering')
    },
    onResize() {
      this.setCSSHeightVariable()
    },
    setCSSHeightVariable() {
      // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    },
  },
  // Needed for SSR rendering
  // https://vapperjs.org/data-prefetching.html#data-prefetch
  // BUT does not work with composition-api + defineComponent
  needSerialize: true,
  async created() {
    this.$logger.trace('[app:created]', this.$$type)

    if (!this.$isServer) {
      this.hasError =
        document.querySelector('[data-server-rendered].error') !== null
    }

    if (this.hasError) {
      return
    }

    // Pass option to root context…
    // More convenient because $options.$storage could be undefined
    this.$root.$storage = this.$root.$options.$storage as StorageUtils

    await this.fetchChrome({ lang: this.$i18n.locale, type: this.$$type })

    // Get API response serialized (thanks to `needSerialize`)
    // Or from router (e.g.: SSR error, SPA Fallback)
    // Init store resource
    if (this.$isServer) {
      this.setResource(this.$root.$options.$resource)
    } else if (!this.$isServer && document.body.dataset.rendering === 'SPA') {
      this.setResource(this.$router.$resource)
    }
  },
  mounted() {
    this.$logger.trace('[app:mounted]')
    this.body = document.body
    this.$root.$on('scroll-disabled', () =>
      this.body.classList.add('no-scroll')
    )
    this.$root.$on('scroll-enabled', () =>
      this.body.classList.remove('no-scroll')
    )

    // Upon first visit, if we're not on homepage, flag onboarding as DONE.
    // It won't run
    if (this.$route.name !== 'Home') {
      this.flagOnboarding()
    }

    const scrollbarWidth = getScrollBarWidth()
    document.body.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`)

    this.setCSSHeightVariable()

    if (this.meta && this.meta.meta) {
      this.meta.meta.push({ name: 'environment', content: VUE_APP_ENV })
    }

    window.addEventListener('resize', this.onResize)
  },
  watch: {
    $route() {
      // Update store
      this.setResource(this.$router.$resource)
    },
  },
  head() {
    return {
      ...this.meta,
      link: favicon?.link.concat(this.meta?.link || []),
      meta: favicon?.meta.concat(this.meta?.meta || []),
    }
  },
})
